import { useMemo } from "react";
import { DataSource } from "../types";
import useUploadedPapers from "features/ResearchHub/hooks/useUploadedPapers";
import useLabels from "hooks/useLabels";
import { formatPaperDescription } from "../utils/formatPaperDescription";
import { useBookmarkLists } from "./useBookmarkLists";
import { IBookmarkListItem } from "helpers/bookmark";
import { useAllUploadingFiles } from "features/ResearchHub/hooks/useAllUploadingFiles";
import { FeatureFlag, useFeatureGate } from "features/Statsig";

const convertBookmarkListToDataSource = (
  list: IBookmarkListItem,
  collectionDescriptionTemplate: string
): DataSource => ({
  id: list.id,
  type: "collection",
  title: list.text_label,
  description: collectionDescriptionTemplate.replace(
    "{date}",
    new Date(list.created_at).toLocaleDateString()
  ),
});

const useDataSources = () => {
  const { papers, isLoading: isLoadingPapers } = useUploadedPapers();
  const [paperMetadataLabels] = useLabels("paper-metadata");
  const [dataSourceLabels] = useLabels("features.threads.data-source-selector");
  const uploadingFiles = useAllUploadingFiles();

  const isChatWithCollectionEnabled = useFeatureGate(
    FeatureFlag.CHAT_WITH_COLLECTION
  ).value;

  const { bookmarkLists, isLoading: isLoadingBookmarkLists } = useBookmarkLists(
    isChatWithCollectionEnabled
  );

  const dataSources = useMemo(() => {
    // Create data sources for uploading files
    const uploadingPaperSources: DataSource[] = uploadingFiles.map((file) => ({
      id: `uploading-${file.filename}`,
      type: "uploading-paper" as const,
      title: file.filename,
      description: dataSourceLabels["uploading"],
      paper: null,
    }));

    const uploadedPaperSources: DataSource[] = papers
      .filter((paper) => paper.title)
      .map((paper) => ({
        id: paper.paper_id,
        type: "paper" as const,
        title: paper.title,
        description: formatPaperDescription(
          paper,
          paperMetadataLabels["et-al"]
        ),
        paper: paper,
      }));

    const collectionDescriptionTemplate =
      dataSourceLabels["collection-description"];
    const bookmarkListSources = isChatWithCollectionEnabled
      ? bookmarkLists.map((list) =>
          convertBookmarkListToDataSource(list, collectionDescriptionTemplate)
        )
      : [];

    return [
      ...uploadingPaperSources,
      ...uploadedPaperSources,
      ...bookmarkListSources,
    ];
  }, [
    papers,
    bookmarkLists,
    paperMetadataLabels,
    dataSourceLabels,
    uploadingFiles,
    isChatWithCollectionEnabled,
  ]);

  return {
    dataSources,
    isLoading: isLoadingPapers || isLoadingBookmarkLists,
  };
};

export default useDataSources;
